import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ProcessRunStatus } from '@vivport/backend/types';
import type { MissionControlCancelRunResponse, ProcessRunRefKind } from '@vivport/backend/types/mission-control';
import { useState } from 'react';
import { toast } from 'sonner';
import { cancelProcessRun } from '@/api/processes-api';
import { Button } from '@/components/ui/button';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';

const CANCELLABLE_PROCESS_RUN_STATUSES: ReadonlySet<ProcessRunStatus> = new Set<ProcessRunStatus>([
  'QUEUED',
  'RUNNING',
  'WAITING_APPROVAL',
]);

export function isCancellableProcessRunStatus(status: ProcessRunStatus): boolean {
  return CANCELLABLE_PROCESS_RUN_STATUSES.has(status);
}

interface ProcessRunCancelActionProps {
  runId: string | number;
  kind: ProcessRunRefKind;
  status: ProcessRunStatus;
}

export function ProcessRunCancelAction({ runId, kind, status }: ProcessRunCancelActionProps) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<MissionControlCancelRunResponse['outcome'] | null>(null);
  const cancelMutation = useMutation({
    mutationFn: () => cancelProcessRun(runId, kind),
    onSuccess: async (result) => {
      setOutcome(result.outcome);
      await queryClient.invalidateQueries({ queryKey: ['process-runs'] });
      await queryClient.invalidateQueries({ queryKey: ['processes'] });
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Cancel failed.'));
    },
  });

  if (outcome !== null) {
    return <span className="text-xs text-muted-foreground">{outcome.replace(/_/g, ' ')}</span>;
  }
  if (!isCancellableProcessRunStatus(status)) {
    return null;
  }
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-6 px-2 text-xs"
      disabled={cancelMutation.isPending}
      onClick={() => cancelMutation.mutate()}
    >
      Cancel
    </Button>
  );
}
